import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    Validators
} from '@angular/forms';
import { AsYouType, CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { Unmasks } from '../../../constants/mask.constants';
import { BaseFormControlComponent } from '../base-form-control.component';
import {
    CountryFormattedPhoneNumberMaxLength,
    CountryNames,
    PhoneCountryCodes
} from './phone-input-constants';

interface CountryCodeOption {
    countryIsoCode: CountryCode;
    countryName: string;
    countryCode: string;
    formattedMaxLength: number;
}

@Component({
    selector: 'lsq-phone-input',
    template: `
        <div class="lsq-phone-input" (clickOutside)="handleClickOutside()">
            <label
                *ngIf="!hasInnerLabel"
                class="p label"
                [style.marginBottom]="'1rem'"
                [for]="name"
            >
                {{ label }}
            </label>
            <div
                *ngIf="selectedCountryCodeOption"
                class="phone-input lsq-form-control"
                [class.focused]="isFocused"
                [class.old-styling]="hasInnerLabel"
                [class.has-error]="showErrorMessage(shouldValidate())"
                (click)="handleFocus()"
            >
                <div class="country-code-select" (click)="handleCountryCodeSelectClick()">
                    <div class="country-code p body text-dark-gray">
                        {{ selectedCountryCodeOption.countryIsoCode }} +{{
                            selectedCountryCodeOption.countryCode
                        }}
                    </div>
                    <div>
                        <lsq-icon
                            [style.pointerEvents]="'none'"
                            class="dropdown-chevron"
                            [class.active]="countryDropdownIsOpen"
                            [type]="countryDropdownIsOpen ? icons.ChevronUp : icons.ChevronDown"
                        ></lsq-icon>
                    </div>
                </div>

                <div class="phone-number-input">
                    <lsq-base-input
                        [name]="name"
                        [label]="hasInnerLabel ? label : null"
                        [value]="phoneInputValue"
                        [placeholder]="placeholder"
                        [maxLength]="selectedCountryCodeOption.formattedMaxLength"
                        type="tel"
                        autocomplete="tel"
                        [unmask]="phoneNumberUnmask"
                        [digitsOnly]="true"
                        [hasError]="showErrorMessage(shouldValidate())"
                        (valueChanged)="handlePhoneInputValueChanged($event)"
                        (blurred)="handleBlur()"
                    ></lsq-base-input>
                </div>
            </div>
            <div
                class="lsq-form-control-error-message"
                *ngIf="showErrorMessage(shouldValidate()) && !hasInnerLabel"
            >
                {{ getErrorMessage() }}
            </div>
            <h6
                class="input-error-message text-uppercase font-weight-bold"
                [style.marginBottom]="'1.5rem'"
                *ngIf="showErrorMessage(shouldValidate()) && hasInnerLabel"
            >
                {{ getErrorMessage() }}
            </h6>

            <div *ngIf="countryDropdownIsOpen" class="country-code-dropdown">
                <lsq-input
                    [placeholder]="'Search'"
                    [(ngModel)]="countrySearchModel"
                    (ngModelChange)="handleSearchValueChanged()"
                    name="country-code-search"
                    [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                >
                </lsq-input>
                <div class="country-code-options" style="overflow: auto;">
                    <div
                        class="country-code-option"
                        *ngFor="let countryCodeOption of countryCodeFilteredOptions"
                        [class.active]="countryCodeOption === selectedCountryCodeOption"
                        (click)="handleCountryCodeOptionClicked(countryCodeOption)"
                    >
                        <div class="country-name p body text-dark-gray">
                            {{ countryCodeOption.countryName }}
                        </div>
                        <div class="p body text-dark-gray">
                            +{{ countryCodeOption.countryCode }}
                        </div>
                    </div>

                    <div
                        *ngIf="countryCodeFilteredOptions.length === 0"
                        class="country-code-no-results p body text-dark-gray"
                    >
                        No Results
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: [
        '../common-styles/lsq-common-form-control.scss',
        '../common-styles/lsq-form-control-dropdown.scss',
        './lsq-phone-input.component.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqPhoneInputComponent),
            multi: true
        }
    ]
})
export class LsqPhoneInputComponent extends BaseFormControlComponent<any>
    implements OnInit, ControlValueAccessor {
    @Input() public name!: string;
    @Input() public label!: string;
    @Input() public placeholder?: string;
    @Input() public hasInnerLabel = false;

    public isFocused = false;
    public hasInitialBlurHappened = false;
    public countryDropdownIsOpen = false;

    public countryCodeOptions: CountryCodeOption[] = [];
    public countryCodeFilteredOptions: CountryCodeOption[] = [];
    public selectedCountryCodeOption?: CountryCodeOption;

    public countrySearchModel = '';
    public phoneInputValue = '';

    public readonly icons = Icons;
    public readonly color = Color;
    public readonly phoneNumberUnmask = Unmasks.PhoneUnmask;

    public ngOnInit(): void {
        this.countryCodeOptions = Object.keys(PhoneCountryCodes).map(
            (countryIsoCode: CountryCode) => ({
                countryIsoCode,
                countryName: CountryNames[countryIsoCode],
                countryCode: PhoneCountryCodes[countryIsoCode],
                formattedMaxLength: CountryFormattedPhoneNumberMaxLength[countryIsoCode]
            })
        );
        this.countryCodeFilteredOptions = this.countryCodeOptions;

        // Default to US selected.
        this.selectedCountryCodeOption = this.countryCodeOptions.find(
            (option) => option.countryIsoCode === 'US'
        );

        // If the form control has an initial value, set it.
        if (this.group?.get(this.formControlName)?.value) {
            this.handlePhoneInputValueChanged({
                value: this.group.get(this.formControlName).value?.replace(Unmasks.PhoneUnmask, ''),
                isAutofilled: true
            });
        } else {
            this._updateFormControlValidators();
        }
    }

    public handleCountryCodeSelectClick(): void {
        this.countryDropdownIsOpen = !this.countryDropdownIsOpen;
    }

    public handleCountryCodeOptionClicked(option: CountryCodeOption): void {
        this.selectedCountryCodeOption = option;
        this.countryDropdownIsOpen = false;

        this._updateFormControlValidators();
        this.handlePhoneInputValueChanged({
            value: this.phoneInputValue.replace(this.phoneNumberUnmask, ''),
            isAutofilled: false
        });
    }

    public handlePhoneInputValueChanged({
        value,
        isAutofilled
    }: {
        value: string;
        isAutofilled: boolean;
    }): void {
        let unmaskedInputValue = value;
        if (!unmaskedInputValue) {
            this.phoneInputValue = '';
            this.handleChange('');
            return;
        }

        if (isAutofilled) {
            unmaskedInputValue = this._handleAutofilledAndDetectCountryCode(unmaskedInputValue);
        }

        // Here we take the unmasked value from the input and format it using libphonenumber.
        // The formatted value is then set as input's display value, but we store the unformatted
        // value with the country code on the formControl.
        const formatter = new AsYouType(this.selectedCountryCodeOption.countryIsoCode);
        const formattedPhoneNumber = formatter.input(unmaskedInputValue);

        this.phoneInputValue = formattedPhoneNumber;
        if (isAutofilled) {
            return;
        }
        this.handleChange(`+${this.selectedCountryCodeOption.countryCode}${unmaskedInputValue}`);
        this._updateFormControlValidators();
    }

    // If the value was autofilled, it might have the country code prefixed. We can try to parse it,
    // and if we get a result we can automatically update the country code selection. We also want
    // to sanitize the value that goes in the input display so it does not contain the country code.
    private _handleAutofilledAndDetectCountryCode(value: string): string {
        if (isValidPhoneNumber(`+${value}`)) {
            const phoneNumberData = parsePhoneNumber(`+${value}`);

            const foundCountryCodeOption = this.countryCodeOptions.find(
                (option) => option.countryIsoCode === phoneNumberData.country
            );

            // If they autofilled a number with a country code we do not support, do not change the
            // country code dropdown and do not do any formatting.
            if (!foundCountryCodeOption) {
                return value;
            }

            this.selectedCountryCodeOption = foundCountryCodeOption;
            this._updateFormControlValidators();

            return phoneNumberData.formatNational().replace(this.phoneNumberUnmask, '');
        }
        return value;
    }

    public handleSearchValueChanged(): void {
        this.countryCodeFilteredOptions = this.countryCodeOptions.filter((option) =>
            option.countryName.toLowerCase().includes(this.countrySearchModel.toLowerCase())
        );
    }

    public handleFocus(): void {
        this.isFocused = true;
    }
    public handleBlur(): void {
        this.isFocused = false;
        this.hasInitialBlurHappened = true;
    }

    public handleClickOutside(): void {
        this.isFocused = false;
        this.countryDropdownIsOpen = false;
    }

    public shouldValidate(): boolean {
        return !this.isFocused || this.hasInitialBlurHappened;
    }

    // Use libphonenumber to dynamically add a form control validator for the selected country.
    private _updateFormControlValidators(): void {
        if (this.formControlName && this.group) {
            const formControl = this.group.get(this.formControlName);
            const isRequired = this._determineIfControlHasRequiredValidator(formControl);

            const newValidators = [];

            if (isRequired) {
                newValidators.push(Validators.required);
            }

            const phoneValidator = (control: AbstractControl): { [key: string]: any } | null => {
                if (
                    control.value &&
                    !isValidPhoneNumber(
                        control.value,
                        this.selectedCountryCodeOption.countryIsoCode
                    )
                ) {
                    return {
                        invalidPhoneFormat: {
                            message: this.i18n('Please enter a valid phone number')
                        }
                    };
                }

                return null;
            };

            newValidators.push(phoneValidator);

            formControl.setValidators(newValidators);
        }
    }

    // Unfortunately, Angular 11 does not provide to add a validator to the existing list of validators
    // on a FormControl, nor a way to check what the current validators are. Until we upgrade to Angular 12,
    // we must resort to this hacky way of checking if the required validator existed or not.
    // Essentially you run the validation on an empty string, and see if the results indicate that
    // the required validator failed.
    // Adapted from https://stackoverflow.com/a/43904237
    // The Angular 12 function that would solve this for us: https://angular.io/api/forms/AbstractControl#addValidators
    private _determineIfControlHasRequiredValidator(formControl: AbstractControl): boolean {
        if (!!formControl.validator) {
            const validationResults = formControl.validator(('' as unknown) as AbstractControl);
            return (
                !!validationResults &&
                Object.keys(validationResults).some((validationKey) => validationKey === 'required')
            );
        }
        return false;
    }
}
